import { useCallback, useMemo, useState } from 'react';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Column, ConfirmPopup, Pressable, Row } from '~/commonComponents';
import Portal from '~/commonComponents/Portal';
import { InviteProfessionalPopup } from '~/commonComponents/InviteProfessional/Popup';
import { IUser } from '~/models';
import { useAppSelector } from '~/hooks/useAppSelector';
import { OutlinePlusIcon, OutlineXIcon, SolidMailIcon } from '@tempmee/react-icons';
import Tooltip from '../../../../Tooltip';
import { fireShiftRequestInviteClicked, fireInviteSent, type TInviteSentSource } from '~/posthogs/events';
import { CollapsibleRow } from '../CollapsibleRow/CollapsibleRow';
import { Title } from '../CollapsibleRow/Title';

interface IProps {
  defaultProfessionals: string[];
  onConfirm: (values: string[]) => void;
  professionType?: string;
  officeId: string;
  inviteSource?: string;
}

export const InviteRow = ({
  defaultProfessionals = [],
  onConfirm,
  professionType,
  officeId,
  inviteSource,
}: IProps) => {
  const favoriteProfessionals = useAppSelector((state) => state.user.favoriteProfessionals ?? []);
  const recentProfessionals = useAppSelector((state) => state.user.recentProfessionals ?? []);
  const recommendedProfessionals = useAppSelector(
    (state) => state.user.recommendedProfessionals ?? [],
  );

  const professionals = useMemo(
    () => [...favoriteProfessionals, ...recentProfessionals, ...recommendedProfessionals],
    [favoriteProfessionals, recentProfessionals, recommendedProfessionals],
  );

  const [isInvitePopupOpen, setInvitePopupOpen] = useState(false);
  const [deletePopup, setDeletePopup] = useState<{ open: boolean; professional: IUser | null }>({
    open: false,
    professional: null,
  });

  const selectedProfessionals = useMemo<IUser[]>(() => {
    return defaultProfessionals
      .map((id) => {
        const match: any = professionals.find(
          (p: any) => p?.id === id || p?.professional?.id === id,
        );

        if (!match) return null;

        const fullName =
          match?.fullName ??
          match?.fullname ??
          match?.professional?.fullName ??
          match?.professional?.full_name;

        return {
          id,
          full_name: fullName,
        } as IUser;
      })
      .filter((p) => p !== null);
  }, [professionals, defaultProfessionals]);

  const handleAddProfessional = () => {
    fireShiftRequestInviteClicked();
    setInvitePopupOpen(true);
  };

  const handleRemoveProfessional = useCallback(
    (id: string) => {
      onConfirm(defaultProfessionals.filter((professionalId) => professionalId !== id));
    },
    [defaultProfessionals, onConfirm],
  );

  return (
    <>
      <CollapsibleRow
        disabled
        customCardStyles={{ borderColor: colors.neutral[100] }}
        titlePosition="flex-start"
        TitleComponent={() => (
          <Row
            style={{
              alignItems: 'center',
            }}
          >
            <Title text="Invite:" />

            <Typography as="span" color={colors.neutral[500]} style={{ marginLeft: 4 }}>
              (Optional)
            </Typography>

            <div
              style={{
                marginLeft: 4,
                position: 'relative',
                top: '2px',
              }}
            >
              {/* @ts-expect-error - missing prop */}
              <Tooltip content="Please note, invites will not be delivered to professionals on requested dates in which they are unavailable." />
            </div>
          </Row>
        )}
        ValueComponent={() => (
          <div style={{ marginRight: -16 }}>
            {selectedProfessionals?.length ? (
              <Column flex={1} alignItems="flex-end" gap={13}>
                <>
                  {selectedProfessionals.map((professional) => (
                    <Row key={professional.id} alignItems="center">
                      <Typography as="span" textAlign="right" style={{ marginRight: 18 }}>
                        {professional.full_name}
                      </Typography>
                      <Pressable
                        onPress={() => setDeletePopup({ open: true, professional: professional })}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <OutlineXIcon color={colors.secondary[500]} />
                      </Pressable>
                    </Row>
                  ))}
                  <Pressable onPress={handleAddProfessional}>
                    <Row alignItems="center">
                      <OutlinePlusIcon color={colors.secondary[500]} />
                      <Typography as="span" bold size="body_large" color={colors.secondary[500]} style={{ marginLeft: 8 }}>
                        Invite More
                      </Typography>
                    </Row>
                  </Pressable>
                </>
              </Column>
            ) : (
              <Pressable onPress={handleAddProfessional}>
                <Row>
                  <Typography as="span" color={colors.neutral[300]} style={{ marginRight: 14 }}>
                    Add Professionals
                  </Typography>
                  <OutlinePlusIcon color={colors.secondary[500]} />
                </Row>
              </Pressable>
            )}
          </div>
        )}
        icon={<SolidMailIcon size={24} color={colors.secondary[500]} />}
      />

      <Portal>
        {deletePopup.open && deletePopup.professional && (
          <ConfirmPopup
            title="Remove Professional From Invite"
            description={
              <>
                Are you sure you would like to remove
                <strong>&nbsp;{deletePopup.professional!.full_name}&nbsp;</strong>
                from your request?
              </>
            }
            descriptionStyle={{ width: '95%', marginBottom: 50 }}
            leftButtonText="Go Back"
            leftButtonStyle={{
              backgroundColor: colors.secondary[500],
            }}
            closePopup={() => setDeletePopup({ open: false, professional: null })}
            leftButtonAction={() => setDeletePopup({ open: false, professional: null })}
            rightButtonText="Remove"
            rightButtonAction={() => {
              handleRemoveProfessional(deletePopup.professional!.id);
              setDeletePopup({ open: false, professional: null });
            }}
          />
        )}

        <div onClick={(e) => e.stopPropagation()}>
          <InviteProfessionalPopup
            confirmButtonText="Confirm"
            isOpen={isInvitePopupOpen}
            isRefetchJob={false}
            defaultProfessionals={defaultProfessionals}
            profession={professionType}
            onClose={() => {
              setInvitePopupOpen(false);
            }}
            onConfirm={(ids) => {
              setInvitePopupOpen(false);
              onConfirm(ids);

              if (ids.length > 0) {
                const favoriteCount = ids.filter((id) =>
                  favoriteProfessionals.some((p: any) => p?.id === id),
                ).length;
                const workedWithCount = ids.filter((id) =>
                  recentProfessionals.some(
                    (p: any) => p?.id === id || p?.professional?.id === id,
                  ),
                ).length;
                const newToYouCount = Math.max(0, ids.length - favoriteCount - workedWithCount);

                fireInviteSent({
                  totalInvitesSent: ids.length,
                  favoriteInvitesSent: favoriteCount,
                  workedWithInvitesSent: workedWithCount,
                  newToYouInvitesSent: newToYouCount,
                  source: (inviteSource || 'Shift Request') as TInviteSentSource,
                });
              }
            }}
            officeId={officeId}
            trackingSource="Shift Request"
          />
        </div>
      </Portal>
    </>
  );
};
